export const TIERS = {
  core: {
    label: 'Core',
    priceCents: 15000,
    features: [
      'Individualized Monthly Training Plan',
      '1-on-1 Coaching: Every Other Week',
      'Strategic Race Planning',
      'Recovery Tools and Exercises',
    ],
  },
  performance: {
    label: 'Performance',
    priceCents: 22500,
    features: [
      'Individualized Monthly Training Plan',
      'Adaptive Strength Training Program',
      '1-on-1 Coaching: Every Other Week',
      'Mental Performance Coaching',
      'Technique and Form Videos',
      'Strategic Race Planning',
      'Recovery Tools and Exercises',
    ],
  },
  elite: {
    label: 'Elite',
    priceCents: 27500,
    features: [
      'Individualized Monthly Training Plan',
      'Adaptive Strength Training Program',
      '1-on-1 Coaching: Weekly',
      'Mental Performance Coaching',
      'Customized Nutrition Plan',
      'Technique and Form Videos',
      'Strategic Race Planning',
      'NCAA Recruiting Guidance',
      'Recovery Tools and Exercises',
    ],
  },
};

export const GRACE_DAY = 5;

/** Returns true if training should be locked (past grace period and unpaid) */
export function billingIsLocked(billingRecord) {
  if (billingRecord?.paid_at) return false;
  return new Date().getDate() > GRACE_DAY;
}

export function billingRecordIsPaid(billingRecord) {
  return !!billingRecord?.paid_at;
}

export function billingRecordsArePaid(billingRecords) {
  return (billingRecords ?? []).some(billingRecordIsPaid);
}

export function billingMonthKey(month, year) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function currentBillingMonth() {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

export function dateBillingMonth(date) {
  return { month: date.getMonth() + 1, year: date.getFullYear() };
}

export function compareBillingMonths(a, b) {
  return (a.year * 12 + a.month) - (b.year * 12 + b.month);
}

export function isFutureBillingMonth(month, year) {
  return compareBillingMonths({ month, year }, currentBillingMonth()) > 0;
}

export function futureTrainingAccessIsOpen(plan, billingRecord) {
  return plan?.status === 'sent' && billingRecordIsPaid(billingRecord);
}

export function futureTrainingAccessIsOpenForBillingRows(plan, billingRecords) {
  return plan?.status === 'sent' && billingRecordsArePaid(billingRecords);
}

export function futureBillingMonthsThrough(targetMonth, targetYear) {
  const current = currentBillingMonth();
  if (compareBillingMonths({ month: targetMonth, year: targetYear }, current) <= 0) {
    return [];
  }

  const months = [];
  let cursor = { month: current.month + 1, year: current.year };
  if (cursor.month > 12) cursor = { month: 1, year: cursor.year + 1 };

  while (compareBillingMonths(cursor, { month: targetMonth, year: targetYear }) <= 0) {
    months.push(cursor);
    cursor = { month: cursor.month + 1, year: cursor.year };
    if (cursor.month > 12) cursor = { month: 1, year: cursor.year + 1 };
  }

  return months;
}

export function formatPrice(cents) {
  return `$${(cents / 100).toLocaleString()}`;
}

export function billingMonthLabel(month, year) {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Returns the effective price for an athlete.
 * If the athlete has a custom (grandfathered) price stored on their profile,
 * that always wins. Otherwise falls back to the tier default.
 */
export function effectivePrice(tierKey, customPriceCents) {
  if (customPriceCents != null) return customPriceCents;
  return TIERS[tierKey]?.priceCents ?? 0;
}
